
import defaultTokenImage from './imgs/defaultTokenImage.png';
import BigNumber from 'bignumber.js';

const API_KEY = process.env.REACT_APP_API_KEY;
const API_SECRET = process.env.REACT_APP_API_SECRET;

export function areMapsEqual(map1, map2) {
  if (map1.size !== map2.size) return false;
  for (let [key, value] of map1.entries()) {
    const value2 = map2.get(key);
    // Compare by JSON.stringify (or a more refined comparison if needed)
    if (!value2 || JSON.stringify(value) !== JSON.stringify(value2)) {
      return false;
    }
  }
  return true;
}

export const floorToDecimals = (value, decimals) => {
  return value.decimalPlaces(decimals, BigNumber.ROUND_DOWN).toFixed(decimals);
};

export const calculateSafeFillVal = (humanBal, pct, decimals) => {
  let fillVal = humanBal.times(pct);

  // Define epsilon based on token decimals (e.g., 1e-6 for 6 decimals)
  const epsilon = new BigNumber(`0.${'0'.repeat(decimals - 1)}1`);

  // If percentage is 100%, subtract epsilon
  if (pct === 1) {
    fillVal = fillVal.minus(epsilon);
  }

  // Floor to the specified decimals
  fillVal = floorToDecimals(fillVal, decimals);

  return fillVal;
};

export const generateColorPalette = (numColors) => {
    const colors = [];
    const hueStep = Math.floor(360 / numColors);
    for (let i = 0; i < numColors; i++) {
        const hue = i * hueStep;
        colors.push(`hsl(${hue}, 70%, 50%)`);
    }
    return colors;
};


export const chunkArray = (arr, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        chunks.push(arr.slice(i, i + chunkSize));
    }
    return chunks;
};


export const formatCoolDownTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let result = '';
  if (hours > 0) {
      result += `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  if (minutes > 0) {
      result += `${hours > 0 ? ', ' : ''}${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  if (remainingSeconds > 0 || result === '') {
      result += `${hours > 0 || minutes > 0 ? ', ' : ''}${remainingSeconds} second${remainingSeconds > 1 ? 's' : ''}`;
  }

  return result;
};

export const getTokenPrice = (tokensList, tokenLabel = '', tokenAddress = '') => {
  // Use the provided label or address to find the token in the list
  if (tokensList === undefined) {
    return 0;
  }
  const token = tokensList.find(token =>
    (tokenLabel && token.label === tokenLabel) ||
    (tokenAddress && token.address.toLowerCase() === tokenAddress.toLowerCase())
  );

  // Return the token's price, or undefined if it wasn't found
  return token?.price;
};

export const getTokenDecimals = (tokensList, tokenLabel = '', tokenAddress = '') => {
  // Use the provided label or address to find the token in the list
  const token = tokensList.find(token =>
    (tokenLabel && token.label === tokenLabel) ||
    (tokenAddress && token.address.toLowerCase() === tokenAddress.toLowerCase())
  );

  // Return the token's decimals, or undefined if it wasn't found
  return token?.decimals;
};

export const formatDecimals = (value, decimals) => {
  const dividedValue = value / (10 ** decimals);
  return dividedValue === 0 ? '0' : abbreviateNumber(dividedValue);
};

export const formatPercentage = (dividedValue) => {
  if (dividedValue === 0) {
    return '0%';
  }

  // Determine the magnitude of the value
  const magnitude = Math.log10(Math.abs(dividedValue * 100));

  let fixedValue;
  if (magnitude < -4) { // Smaller than 0.0001%
    fixedValue = (dividedValue * 100).toExponential(2);
  } else if (magnitude < 1) { // Less than 10%
    fixedValue = (dividedValue * 100).toFixed(2);
  } else if (magnitude < 2) { // Less than 100%
    fixedValue = (dividedValue * 100).toFixed(1);
  } else {
    // For large numbers, reduce the number of decimal places as the number gets larger
    const decimals = Math.max(0, 2 - Math.floor(magnitude));
    fixedValue = (dividedValue * 100).toFixed(decimals);
  }

  return `${fixedValue}%`;
};

export const formatBalanceWithLogo = (balance, token, data) => {
    return formatBalanceWithIcon(
        balance,
        token?.address,
        data,
        token?.decimals
    )
};

export const formatCurrencyWithLogo = (usdAmount, token, data) => {
    return formatUSDBalanceWithIcon(
        usdAmount,
        token?.address,
        data
    )
};

export const formatWalletAddress = (str) => {
  if (!str) {
    return "";
  }
  return str.length <= 4 ? str : str.slice(0, 5) + "..." + str.slice(-4);
};

export const abbreviateNumber = (n) => {
  if (n < 1e-6) return n.toExponential(2);
  if (n < 1e-5 && n >= 1e-6) return n.toFixed(7);
  if (n < 1e-4 && n >= 1e-5) return n.toFixed(6);
  if (n < 0 && n >= 1e-3) return n.toFixed(4);
  if (n === 0) return n.toFixed(2);
  if (n < 1) return n.toFixed(5);
  if (n < 1e3) return n.toFixed(4);
  if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(2) + "K";
  if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(2) + "M";
  if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(2) + "B";
  if (n >= 1e12) return +(n / 1e12).toFixed(2) + "T";
};

export const formatCurrency = (value) => {
  return '$' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
export const formatCurrencyExtended = (value) => {
  const numValue = Number(value);

  // Check if the value is NaN or not a finite number
  if (isNaN(numValue) || !isFinite(numValue)) {
    return '$0.00';
  }

  // For values >= 1, format with two decimal places and include commas
  if (numValue >= 1) {
    return '$' + numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Convert the number to a string
  let decimalStr = numValue.toString();

  // Handle very small numbers that are in scientific notation
  if (/e-/.test(decimalStr)) {
    const match = decimalStr.match(/e-(\d+)/);
    if (match) {
      const zerosCount = parseInt(match[1], 10) - 1;
      decimalStr = '0.' + '0'.repeat(zerosCount) + decimalStr.split('e-')[0].replace('.', '');
    }
  }

  // Extract decimal part after removing scientific notation
  const decimalPart = decimalStr.split('.')[1] || '';

  // Determine the number of decimal places to show based on the value
  let decimalPlacesToShow;
  if (numValue >= 0.1) {
    decimalPlacesToShow = 4;
  } else if (numValue >= 0.01) {
    decimalPlacesToShow = 5;
  } else if (numValue >= 0.001) {
    decimalPlacesToShow = 6;
  } else if (numValue >= 0.00000001) {
    decimalPlacesToShow = 8;
  } else if (numValue >= 0.000000001) {
    decimalPlacesToShow = 9;
  } else if (numValue >= 0.0000000001) {
    decimalPlacesToShow = 10;
  }
  else {
    //decimalPlacesToShow = Math.min(decimalPart.length, 10);
    //default to $0 for very small numbers
    decimalPlacesToShow = 2;
  }

  // Format the number with the determined decimal places
  return '$' + parseFloat(decimalStr).toLocaleString(undefined, { minimumFractionDigits: decimalPlacesToShow, maximumFractionDigits: decimalPlacesToShow });
};


export const formatValue = (value, decimals, isPercentage) => {
  const dividedValue = value / (10 ** decimals);
  if (isPercentage) {
    return dividedValue.toFixed(2) + '%';
  }
  return dividedValue === 0 ? '0' : abbreviateNumber(dividedValue);
};

export const dateTickFormatter = (timestamp) => {
  const dateObject = new Date(timestamp);
  const formattedDate = `${dateObject.getMonth() + 1}/${dateObject.getDate()}/${dateObject.getFullYear()}`;
  return formattedDate;
}

export const formatValueRaw = (value, decimals, isPercentage) => {
  const dividedValue = value / (10 ** decimals);
  if (isPercentage) {
    return dividedValue.toFixed(2) + '%';
  }
  return dividedValue;
};

export const formatAddress = (address) => {
  if (!address) {
    return "";
  }
  const firstFive = address.slice(0, 5);
  const lastFour = address.slice(-4);
  const url = `https://bscscan.com/address/${address}`;
  return `<a href="${url}" target="_blank" rel="noopener noreferrer">${firstFive}...${lastFour}</a>`;
};


export const formatTokenIcon = (dbData, address) => {
  if (!dbData || address === undefined || address === null || address === ""
  ) return null;
  let logoUrl = null;
  const tokenData = dbData?.find((token) => token.address.toLowerCase() === address.toLowerCase());
  //console.log("tokenData", tokenData);
  logoUrl = tokenData && tokenData.logo != null ? tokenData.logo.url : null;
  //console.log("logoUrl:", logoUrl);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {logoUrl && (
        <img
          src={logoUrl} //https://floatie-001.s3.amazonaws.com/56/icon/
          alt={tokenData?.symbol}
          style={{ width: `20px`, marginRight: '5px', marginLeft: '1px' }}
        />
      )}
    </span>
  );
};

export const formatBalanceWithIcon = (balance, tokenAddress, dbData, decimals) => {
  const tokenIcon = formatTokenIcon(dbData, tokenAddress);
  const formattedBalance = formatDecimals(balance, decimals);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {formattedBalance} {tokenIcon}
    </div>
  );
};

export const formatUSDBalanceWithIcon = (usdAmount, tokenAddress, dbData) => {
  const tokenIcon = formatTokenIcon(dbData, tokenAddress);
  const formattedBalance = formatCurrencyExtended(usdAmount);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {formattedBalance} {tokenIcon}
    </div>
  );
};


export const formatBalanceWithIcon2 = (balance, dbData, option, decimals) => {
  // Check if option is an array. If it is, join the labels and return.
  if (Array.isArray(option)) {
    const labels = option.map(opt => opt.label);
    return labels.join(', ');
  }

  const { value, label } = option;

  if (!value) {
    return null;  // or some fallback UI
  }

  const baseUrl = "https://floatie-001.s3.amazonaws.com/56/icon/";

  let logoUrl = null;
  if (dbData) {
    const tokenData = dbData.find((token) => token.address.toLowerCase() === value.toLowerCase() || token.address === value || token.symbol === label);
    //logoUrl = tokenData?.logo?.url || `${baseUrl}${value}.png`;
    //logoUrl = tokenData && tokenData.logo ? tokenData.logo.url : tokenData?.logo?.url || `${baseUrl}${value.toLowerCase()}.png`;
    logoUrl = tokenData && tokenData.logo ? tokenData.logo.url : tokenData?.logo?.url || `${baseUrl}${value}.png`;
  }


  const formattedBalance = formatDecimals(balance, decimals);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {formattedBalance}
      {logoUrl && (
        <img
          src={logoUrl}
          alt={label}
          style={{ width: `20px`, marginLeft: '4px', marginRight: '8px' }}
        />
      )}
    </div>
  );
};

//returns the token logo url or default token image
export const getTokenLogo = (dbData, option) => {
  let logoUrl = null;
  if (dbData) {
    const tokenData = dbData.find((token) => token?.address?.toLowerCase() === option?.address?.toLowerCase());
    logoUrl = tokenData && tokenData.logo ? tokenData.logo.url : defaultTokenImage;
  }
  return logoUrl;
};

// export const getTokenLogoByAddress = (address) => {
//   //return either https://floatie-001.s3.amazonaws.com/56/icon/address.png or https://floatie-001.s3.amazonaws.com/56/icon/address.toLowerCase().png
//   //detect if https://floatie-001.s3.amazonaws.com/56/icon/address.png is a valid url
//   console.log("address:", address)
//   let url = `https://floatie-001.s3.amazonaws.com/56/icon/${address}.png`;
//   const img = new Image();
//   img.src = url;
//   if (img.height == 0) {
//     url = `https://floatie-001.s3.amazonaws.com/56/icon/${address.toLowerCase()}.png`;
//   }

//   return <span style={{ display: 'inline-flex', alignItems: 'center' }}>
//     {url && (
//       <img
//         src={url}
//         style={{ width: `20px`, marginRight: '5px', marginLeft: '1px' }}
//       />
//     )}
//   </span>
// };

export const formatTokenLabel = (dbData, option) => {
  // Check if option is an array. If it is, join the labels and return.
  if (Array.isArray(option)) {
    const labels = option.map(opt => opt.label);
    return labels.join(', ');
  }

  const { value, label } = option;

  if (!value) {
    return null;  // or some fallback UI
  }

  const baseUrl = "https://floatie-001.s3.amazonaws.com/56/icon/";

  let logoUrl = null;
  if (dbData) {
    const tokenData = dbData.find((token) => token.address.toLowerCase() === value.toLowerCase());
    //logoUrl = tokenData && tokenData.logo ? tokenData.logo.url : tokenData?.logo?.url || `${baseUrl}${value.toLowerCase()}.png`;
    logoUrl = tokenData && tokenData.logo ? tokenData.logo.url : tokenData?.logo?.url || `${baseUrl}${value}.png`;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {logoUrl && (
        <img
          src={logoUrl}
          alt={label}
          style={{ width: `20px`, marginRight: '8px' }}
        />
      )}
      <span>{label}</span>
    </div>
  );
};



export const formatTokenLabelExtended = (dbData, option) => {
  // Check if option is an array. If it is, join the labels and return.
  if (Array.isArray(option)) {
    const labels = option.map(opt => opt.label);
    return labels.join(', ');
  }

  const { value, label, name } = option;

  if (!value) {
    return null;  // or some fallback UI
  }

  let logoUrl = null;
  if (dbData) {
    const tokenData = dbData.find((token) => token.address.toLowerCase() === value.toLowerCase());
    logoUrl = tokenData && tokenData.logo ? tokenData.logo.url : defaultTokenImage;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {logoUrl && (
        <img
          src={logoUrl}
          alt={label}
          style={{ width: `20px`, marginRight: '8px' }}
        />
      )}
      <>{label} {' '}{name}{' '}</>
    </div>
  );
};



export const formatOptionLabel = (dbData, option) => {
  const { value, label } = option;
  let logoUrl = null;
  if (dbData) {
    const tokenData = dbData.find((token) => token.address.toLowerCase() === value.toLowerCase());
    logoUrl = tokenData && tokenData.logo ? tokenData.logo.url : defaultTokenImage;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {logoUrl && (
        <img
          src={logoUrl}
          alt={label}
          style={{ width: `20px`, marginRight: '8px' }}
        />
      )}
      <span>{label}</span>
    </div>
  );
};

export const formatTokenOptionLabel = (dbData, option) => {
  if (!option || !option.address) return '';
  const { name, symbol, address } = option;
  const tokenData = dbData[address.toLowerCase()] || {};
  const { logoURI } = tokenData;
  return (
    <div className="p-d-flex p-ai-center">
      {logoURI && (
        <img
          src={logoURI}
          alt={symbol}
          style={{ width: '24px', marginRight: '8px' }}
        />
      )}
      {symbol || name}
    </div>
  );
};


const createAuthHeaders = () => {
  return {
      'Content-Type': 'application/json',
      'x-floatie-api-key': API_KEY,
      'x-floatie-api-secret': API_SECRET,
  };
};

export async function updateToken(token) {
  console.log('Updating token:', token);

  try {
      const response = await fetch(`https://api.rocketfi.app/v1/tokens/${token.address}`, {
          method: 'PUT',
          headers: createAuthHeaders(),
          body: JSON.stringify(token),
      });

      const data = await response.json();
      console.log('Response from token update:', data);

      if (data.status !== 200) {
          console.error('Token update failed with status:', data.status);
      } else {
          console.log('Token updated successfully');
      }
  } catch (error) {
      console.error('Error in updateToken:', error);
  }
}



